"use client";

import { motion, useScroll, useTransform } from "framer-motion";

// Create smooth wavy lattice pattern with animation
const createSmoothWavyPattern = () => {
  const paths = [];
  const gridSize = 75; // Increased from 50 to 75
  const rows = 12; // Adjusted for larger grid
  const cols = 16; // Adjusted for larger grid

  // Smoother wave parameters for gentle, flowing waves - adjusted for larger grid
  const waveAmplitude = 15; // Increased wave height for larger grid
  const waveFrequency = 0.01; // Slightly lower frequency for smoother waves with larger grid

  // Create smooth horizontal wave lines with draw/erase animation
  for (let i = 0; i < rows; i++) {
    let pathData = "";
    const baseY = i * gridSize;

    for (let x = 0; x <= cols * gridSize; x += 5) {
      const waveY =
        Math.sin(x * waveFrequency + i * 0.3) * waveAmplitude +
        Math.sin(x * waveFrequency * 0.7 + i * 0.2) * (waveAmplitude * 0.3);
      const actualY = baseY + waveY;

      if (x === 0) {
        pathData += `M ${x} ${actualY}`;
      } else {
        // Use smooth curves instead of straight lines
        const prevX = x - 5;
        const prevWaveY =
          Math.sin(prevX * waveFrequency + i * 0.3) * waveAmplitude +
          Math.sin(prevX * waveFrequency * 0.7 + i * 0.2) *
            (waveAmplitude * 0.3);
        const prevY = baseY + prevWaveY;

        const cp1x = prevX + 2.5;
        const cp1y = prevY;
        const cp2x = x - 2.5;
        const cp2y = actualY;

        pathData += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${x.toFixed(2)} ${actualY.toFixed(2)}`;
      }
    }

    paths.push(
      <motion.path
        key={`h-${i}`}
        d={pathData}
        stroke="url(#lilac-gradient)"
        strokeWidth="1.5"
        fill="none"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{
          pathLength: [0, 1, 1, 0],
          opacity: [0, 0.4, 0.4, 0],
        }}
        transition={{
          duration: 4 + i * 0.3,
          repeat: Infinity,
          delay: i * 0.2,
          ease: "easeInOut",
        }}
      />,
    );
  }

  // Create smooth vertical wave lines with draw/erase animation
  for (let j = 0; j < cols; j++) {
    let pathData = "";
    const baseX = j * gridSize;

    for (let y = 0; y <= rows * gridSize; y += 5) {
      const waveX =
        Math.sin(y * waveFrequency * 1.2 + j * 0.4) * (waveAmplitude * 0.6) +
        Math.cos(y * waveFrequency * 0.8 + j * 0.3) * (waveAmplitude * 0.2);
      const actualX = baseX + waveX;

      if (y === 0) {
        pathData += `M ${actualX} ${y}`;
      } else {
        // Use smooth curves for vertical lines too
        const prevY = y - 5;
        const prevWaveX =
          Math.sin(prevY * waveFrequency * 1.2 + j * 0.4) *
            (waveAmplitude * 0.6) +
          Math.cos(prevY * waveFrequency * 0.8 + j * 0.3) *
            (waveAmplitude * 0.2);
        const prevX = baseX + prevWaveX;

        const cp1x = prevX;
        const cp1y = prevY + 2.5;
        const cp2x = actualX;
        const cp2y = y - 2.5;

        pathData += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${actualX.toFixed(2)} ${y.toFixed(2)}`;
      }
    }

    paths.push(
      <motion.path
        key={`v-${j}`}
        d={pathData}
        stroke="url(#blue-gradient)"
        strokeWidth="1.5"
        fill="none"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{
          pathLength: [0, 1, 1, 0],
          opacity: [0, 0.4, 0.4, 0],
        }}
        transition={{
          duration: 4 + j * 0.2,
          repeat: Infinity,
          delay: j * 0.15 + 2,
          ease: "easeInOut",
        }}
      />,
    );
  }

  // Add intersection nodes at wave intersections
  const nodes = [];
  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      const baseX = j * gridSize;
      const baseY = i * gridSize;

      // Calculate exact vertical line position at this Y coordinate
      const waveX =
        Math.sin(baseY * waveFrequency * 1.2 + j * 0.4) *
          (waveAmplitude * 0.6) +
        Math.cos(baseY * waveFrequency * 0.8 + j * 0.3) * (waveAmplitude * 0.2);

      // Calculate exact horizontal line position at this X coordinate
      const waveY =
        Math.sin(baseX * waveFrequency + i * 0.3) * waveAmplitude +
        Math.sin(baseX * waveFrequency * 0.7 + i * 0.2) * (waveAmplitude * 0.3);

      // Position nodes at exact intersection points
      const x = baseX + waveX;
      const y = baseY + waveY;

      nodes.push(
        <circle
          key={`node-${i}-${j}`}
          cx={x.toFixed(2)}
          cy={y.toFixed(2)}
          r="1.5"
          fill="currentColor"
          opacity="0.4"
        />,
      );
    }
  }

  return [...paths, ...nodes];
};

export default function LatticeBackground() {
  const { scrollYProgress } = useScroll();

  // Lattice transformations based on scroll (faster animation)
  const latticeScale = useTransform(scrollYProgress, [0, 0.5], [0.8, 1.4]);
  const latticeRotate = useTransform(scrollYProgress, [0, 1], [0, 12]);
  const latticeOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.6]);

  return (
    <div className="fixed inset-0 pointer-events-none bg-black">
      <motion.div
        style={{
          scale: latticeScale,
          rotate: latticeRotate,
          opacity: latticeOpacity,
          perspective: "1200px",
          transformStyle: "preserve-3d",
        }}
        className="absolute inset-0 overflow-hidden"
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 1200 900"
          className="absolute inset-0"
          style={{
            transform: "rotateX(8deg) rotateY(2deg)",
            transformOrigin: "center center",
          }}
        >
          <defs>
            <linearGradient
              id="lilac-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset="0%" stopColor="#A47DAB" stopOpacity="0" />
              <stop offset="20%" stopColor="#A47DAB" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#A47DAB" stopOpacity="0.7" />
              <stop offset="80%" stopColor="#A47DAB" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#A47DAB" stopOpacity="0" />
            </linearGradient>
            <linearGradient
              id="blue-gradient"
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#60a5fa" stopOpacity="0" />
              <stop offset="20%" stopColor="#93c5fd" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.7" />
              <stop offset="80%" stopColor="#93c5fd" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
            </linearGradient>
          </defs>
          {createSmoothWavyPattern()}
        </svg>
      </motion.div>

      {/* Randomly positioned animated lattice nodes that "breathe" */}
      <motion.div
        style={{
          scale: latticeScale,
          rotate: latticeRotate,
          opacity: latticeOpacity,
        }}
        className="absolute inset-0"
      >
        {[...Array(24)].map((_, i) => {
          // Generate consistent seeded "random" properties for each dot
          const seed = i * 7; // Simple seed based on index
          const randomLeft = ((seed * 17) % 90) + 5; // 5% to 95%
          const randomTop = ((seed * 23) % 90) + 5; // 5% to 95%
          const randomDuration = ((seed * 13) % 20) / 10 + 2; // 2-4 seconds
          const randomDelay = ((seed * 11) % 30) / 10; // 0-3 seconds
          const randomSize = ((seed * 19) % 50) / 100 + 0.75; // 0.75-1.25 scale
          const shapes = [
            "rounded-full", // circles
            "rounded-none", // squares
            "rounded-sm", // slightly rounded squares
            "rounded-md", // more rounded squares
            "", // diamond shape (we'll add custom styles)
            "rounded-tl-full rounded-br-full", // diagonal rounded
            "rounded-tr-full rounded-bl-full", // reverse diagonal rounded
          ];
          const colors = [
            "bg-accent",
            "bg-primary",
            "bg-muted-foreground",
            "bg-blue-400/60",
            "bg-[#A47DAB]/60",
            "bg-green-400/60",
            "bg-yellow-400/60",
          ];
          const randomShape = shapes[seed % shapes.length];
          const randomColor = colors[(seed * 3) % colors.length];

          // Create triangle shape for some dots
          const isTriangle = randomShape === "";
          const triangleSize = randomSize * 8; // Convert to pixels for triangle

          if (isTriangle) {
            return (
              <motion.div
                key={i}
                className="absolute"
                style={{
                  left: `${randomLeft}%`,
                  top: `${randomTop}%`,
                }}
                animate={{
                  scale: [randomSize, randomSize * 1.6, randomSize],
                  opacity: [0.3, 0.8, 0.3],
                }}
                transition={{
                  duration: randomDuration,
                  repeat: Infinity,
                  delay: randomDelay,
                  ease: "easeInOut",
                }}
              >
                <div
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: `${triangleSize}px solid transparent`,
                    borderRight: `${triangleSize}px solid transparent`,
                    borderBottom: `${triangleSize * 1.5}px solid`,
                    borderBottomColor: randomColor?.includes("accent")
                      ? "hsl(var(--accent))"
                      : randomColor?.includes("primary")
                        ? "hsl(var(--primary))"
                        : randomColor?.includes("muted")
                          ? "hsl(var(--muted-foreground))"
                          : randomColor?.includes("blue")
                            ? "rgb(96 165 250 / 0.6)"
                            : randomColor?.includes("purple")
                              ? "rgb(168 85 247 / 0.6)"
                              : randomColor?.includes("green")
                                ? "rgb(74 222 128 / 0.6)"
                                : "rgb(250 204 21 / 0.6)",
                  }}
                />
              </motion.div>
            );
          }

          return (
            <motion.div
              key={i}
              className={`absolute w-2 h-2 ${randomColor} ${randomShape} shadow-sm`}
              style={{
                left: `${randomLeft}%`,
                top: `${randomTop}%`,
                transform: `scale(${randomSize})`,
              }}
              animate={{
                scale: [randomSize, randomSize * 1.6, randomSize],
                opacity: [0.3, 0.8, 0.3],
              }}
              transition={{
                duration: randomDuration,
                repeat: Infinity,
                delay: randomDelay,
                ease: "easeInOut",
              }}
            />
          );
        })}
      </motion.div>
    </div>
  );
}
